import { useState, useEffect, useCallback } from 'react';
import type { VideoItem } from '@/types/educaliec';
import { fetchPlaylistVideos } from '@/lib/youtubeService';

interface UseYouTubePlaylistResult {
  videos: VideoItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook that fetches and manages YouTube playlist videos for EducaLIEC.
 * Calls fetchPlaylistVideos on mount and provides loading/error states.
 * Videos are returned sorted by publishedAt descending (handled by the service).
 */
export function useYouTubePlaylist(): UseYouTubePlaylistResult {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadVideos = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchPlaylistVideos();
      setVideos(result);
    } catch (err) {
      let errorMessage = 'No se pudieron cargar los videos. Intenta de nuevo más tarde.';
      
      if (err instanceof Response) {
        if (err.status === 403) {
          errorMessage = 'Límite de API alcanzado. Los videos se cargarán desde caché si está disponible.';
        } else if (err.status === 400) {
          errorMessage = 'Error de configuración de API. Verifica la clave de API y el ID de playlist.';
        } else {
          errorMessage = `Error de API (${err.status}): ${err.statusText}`;
        }
      } else if (err instanceof Error) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  const refetch = useCallback(() => {
    loadVideos();
  }, [loadVideos]);

  return { videos, loading, error, refetch };
}
